"use client";

import React, { useState } from "react";

interface SectionConfig {
  title?: string | null;
  withPadding?: boolean | null;
  topPadding?: string | null;
  noBottomPadding?: boolean | null;
  bottomPadding?: string | null;
  horizontalPadding?: string | null;
  withHorizontalPadding?: boolean | null;
  withSideLines?: boolean | null;
  withFilledLine?: boolean | null;
  withGap?: boolean | null;
  withInnerGap?: boolean | null;
  narrow?: boolean | null;
  narrowBody?: boolean | null;
  withEdgeLines?: boolean | null;
}

interface BlockInspectorProps {
  blockType: string;
  blockName?: string | null;
  index: number;
  children: React.ReactNode;
  sectionConfig?: SectionConfig;
  isNested?: boolean;
}

export function BlockInspector({
  blockType,
  blockName,
  index,
  children,
  sectionConfig,
  isNested = false,
}: BlockInspectorProps) {
  // React hooks must be called before any conditional returns
  const [isHovered, setIsHovered] = useState(false);

  // Format label content
  let label = blockName ? `${blockType}: "${blockName}" (#${index})` : `${blockType} (#${index})`;

  // Add section config info if available
  if (sectionConfig && blockType === "section") {
    const configParts: string[] = [];

    // Show all padding values
    if (sectionConfig.topPadding) {
      configParts.push(`topPadding:${sectionConfig.topPadding}`);
    }
    if (sectionConfig.bottomPadding) {
      configParts.push(`bottomPadding:${sectionConfig.bottomPadding}`);
    }
    if (sectionConfig.horizontalPadding) {
      configParts.push(`horizontalPadding:${sectionConfig.horizontalPadding}`);
    }

    // Show only true boolean flags
    if (sectionConfig.withPadding) {
      configParts.push("withPadding");
    }
    if (sectionConfig.noBottomPadding) {
      configParts.push("noBottomPadding");
    }
    if (sectionConfig.withHorizontalPadding) {
      configParts.push("withHorizontalPadding");
    }
    if (sectionConfig.withSideLines) {
      configParts.push("withSideLines");
    }
    if (sectionConfig.withFilledLine) {
      configParts.push("withFilledLine");
    }
    if (sectionConfig.withGap) {
      configParts.push("withGap");
    }
    if (sectionConfig.withInnerGap) {
      configParts.push("withInnerGap");
    }
    if (sectionConfig.narrow) {
      configParts.push("narrow");
    }
    if (sectionConfig.narrowBody) {
      configParts.push("narrowBody");
    }
    if (sectionConfig.withEdgeLines) {
      configParts.push("withEdgeLines");
    }

    if (configParts.length > 0) {
      label += ` [${configParts.join(", ")}]`;
    }
  }

  return (
    <div
      style={{ position: "relative" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      {isHovered && (
        <div
          aria-hidden='true'
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            border: "2px dashed rgba(59, 130, 246, 0.8)",
            borderRadius: "4px",
            pointerEvents: "none",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "-1px",
              ...(isNested
                ? {
                    right: "-1px",
                    borderRadius: "0 4px 0 4px",
                  }
                : {
                    left: "-1px",
                    borderRadius: "4px 0 4px 0",
                  }),
              backgroundColor: "rgba(59, 130, 246, 0.95)",
              color: "white",
              fontSize: "11px",
              fontFamily: "monospace",
              padding: "4px 8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
              whiteSpace: "nowrap",
              pointerEvents: "auto",
              cursor: "text",
              userSelect: "all",
            }}
            title='Click to select and copy block name'
          >
            {label}
          </div>
        </div>
      )}
    </div>
  );
}
